import React, { memo, useCallback, useState } from 'react'
import { MCStatus } from 'src/constants/mc/enums'
import { MCCustomer } from 'src/interfaces/mc'
import { Button } from '../shared/Button'
import { Chip } from '../shared/Chip'
import { colors } from 'src/theme'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { CancelSmallMinor, TickMinor } from '@shopify/polaris-icons'
import { ROUTES } from 'src/constants/routes'
import { useNavigate } from 'react-router-dom'
import { SensitiveActionWithPictureModal } from '../shared/SubmitActionWithPictureModal'
import NotFoundImage from 'src/assets/images/not-found.png'
import { DeclineCustomerInvite, EndPartnershipWithCustomer } from 'src/constants/actionTypes'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { showToast } from 'src/utils/toast'
import { useDispatch } from 'react-redux'
import { handleApiError } from 'src/utils/errorHandlers'

const customerStatusMap = {
  [MCStatus.Activated]: {
    color: colors.COLORS_GREEN_500,
    text: 'Accepted',
    bgStyle: tw`bg-green-200 w-fit`,
  },
  [MCStatus.Invited]: {
    color: colors.COLORS_BLUE_500,
    text: 'Required action',
    bgStyle: tw`bg-blue-200 w-fit`,
  },
  [MCStatus.Rejected]: {
    color: colors.SHADES_GREY_500,
    text: 'Rejected',
    bgStyle: tw`bg-[rgba(218, 217, 224, 1)] w-fit`,
  },
  [MCStatus.Deactivated]: {
    color: colors.SHADES_GREY_500,
    text: 'Deactivated',
    bgStyle: tw`bg-[rgba(218, 217, 224, 1)] w-fit`,
  },
}

interface Props {
  customer: MCCustomer
  refreshMCCustomers: () => void
}

const ManageableCustomerComponent = ({ customer, refreshMCCustomers }: Props) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [isLoading, setIsLoading] = useState(false)
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)

  const handleAcceptInvite = useCallback(() => {
    navigate(`${ROUTES.MASTER_CONTRACTOR_ACCEPT_INVITE_URL}/${customer.customerId}`)
  }, [customer])

  const handleOpenConfirm = useCallback(() => {
    setIsConfirmOpen(true)
  }, [])

  const handleCloseConfirm = useCallback(() => {
    setIsConfirmOpen(false)
  }, [])

  const handleDeclineInvite = async () => {
    setIsLoading(true)

    try {
      await createAsyncAction(
        dispatch,
        DeclineCustomerInvite.request({
          customerId: customer.customerId,
        }),
      )

      showToast(`You have declined the invite from ${customer.customer.displayName}.`)

      refreshMCCustomers()
    } catch (err) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleEndPartnership = async () => {
    try {
      await createAsyncAction(
        dispatch,
        EndPartnershipWithCustomer.request({
          customerId: customer.customerId,
        }),
      )

      showToast(`Your partnership with ${customer.customer.displayName} has ended.`)

      refreshMCCustomers()
    } catch (err) {
      handleApiError(err)
    } finally {
      handleCloseConfirm()
      setIsLoading(false)
    }
  }

  return (
    <>
      <div
        css={tw`flex justify-between items-center py-4 border-0 border-b  border-solid border-[#EDEDED]`}
      >
        <div css={tw`flex flex-col gap-1`}>
          <Typography variant="h6">{customer.customer.displayName}</Typography>
          <Chip
            text={customerStatusMap[customer.status].text}
            textColor={customerStatusMap[customer.status].color}
            className={customerStatusMap[customer.status].bgStyle}
          />
        </div>
        <div css={tw`flex items-center gap-2`}>
          {customer.status === MCStatus.Invited && (
            <>
              <Button
                color="light"
                size="small"
                leftIcon={<TickMinor fill={colors.PRIMARY_PURPLE_500} width={20} />}
                onClick={handleAcceptInvite}
                disabled={isLoading}
                className={tw`pl-3 pr-4 py-2`}
              >
                Accept invite
              </Button>

              <Button
                color="white"
                size="small"
                leftIcon={<CancelSmallMinor width={20} />}
                onClick={handleDeclineInvite}
                disabled={isLoading}
                css={tw`w-8 h-8 flex items-center justify-center p-0`}
              />
            </>
          )}

          {customer.status === MCStatus.Activated && (
            <Button
              color="white"
              fullWidth
              size="small"
              onClick={handleOpenConfirm}
              disabled={isLoading}
              rightIcon={<CancelSmallMinor width={20} />}
            >
              End partnership
            </Button>
          )}
        </div>
      </div>
      <SensitiveActionWithPictureModal
        isOpen={isConfirmOpen}
        title={`Are you sure you want to end your partnership with ${customer.customer.displayName}?`}
        text="Ending the partnership will remove all currently linked contractors from the customer's system, and you won't be able to link new contractors moving forward."
        submitLabel="End partnership"
        onClose={handleCloseConfirm}
        onSubmit={handleEndPartnership}
        imageSrc={NotFoundImage}
      />
    </>
  )
}

export const ManageableCustomer = memo(ManageableCustomerComponent)
